import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Navigate, Link } from 'react-router-dom';
import { Menu, X, LogIn, UserPlus, Loader2, Mail, Lock, User } from 'lucide-react';
import { toast } from 'sonner';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Auth Context
const AuthContext = createContext();

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    if (location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
          }
        });

        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else if (response.status === 401) {
          // Session expired or invalid - clear user state
          setUser(null);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [location.pathname]); // Changed from location.hash to location.pathname to re-check on navigation

  const logout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// Auth Callback
function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        const hash = location.hash.substring(1);
        const params = new URLSearchParams(hash);
        const sessionId = params.get('session_id');

        if (!sessionId) {
          navigate('/login');
          return;
        }

        const response = await fetch(`${BACKEND_URL}/api/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId })
        });

        if (!response.ok) throw new Error('Session exchange failed');

        const data = await response.json();
        setUser(data.user);
        navigate('/dashboard', { replace: true });
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/login');
      }
    };

    processSession();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">Completing authentication...</p>
      </div>
    </div>
  );
}

// Protected Route
function ProtectedRoute({ children, requireAdmin = false }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    } else if (!loading && requireAdmin && user?.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, loading, requireAdmin, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user || (requireAdmin && user.role !== 'admin')) {
    return null;
  }

  return children;
}

// Navigation
function Navigation() {
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav className="sticky top-0 z-40 border-b border-white/10 bg-background/85 backdrop-blur-md supports-[backdrop-filter]:bg-background/80 shadow-lg shadow-primary/5">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-3 hover:opacity-90 transition-opacity">
              <span className="text-2xl font-heading font-bold text-foreground">LUCK <span className="text-primary">or</span> SKILLS</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-foreground/80 hover:text-foreground transition-colors font-semibold" data-testid="nav-home">
              Home
            </Link>
            <Link to="/about" className="text-foreground/80 hover:text-foreground transition-colors font-semibold" data-testid="nav-about">
              About
            </Link>
            <Link to="/lessons" className="text-foreground/80 hover:text-foreground transition-colors font-semibold" data-testid="nav-lessons">
              Lessons
            </Link>
            <Link to="/calendar" className="text-foreground/80 hover:text-foreground transition-colors font-semibold" data-testid="nav-calendar">
              Calendar
            </Link>
            <Link to="/poker-hands-guide" className="text-foreground/80 hover:text-foreground transition-colors font-semibold" data-testid="nav-poker-hands">
              Poker Hands
            </Link>
            <Link to="/practice" className="text-foreground/80 hover:text-foreground transition-colors font-semibold" data-testid="nav-practice">
              Practice Hands
            </Link>
            <Link to="/live" className="text-foreground/80 hover:text-foreground transition-colors font-semibold" data-testid="nav-live">
              Live Streams
            </Link>
            <Link to="/community" className="text-foreground/80 hover:text-foreground transition-colors font-semibold" data-testid="nav-community">
              Forum
            </Link>
            <Link to="/contact" className="text-foreground/80 hover:text-foreground transition-colors font-semibold" data-testid="nav-contact">
              Contact
            </Link>
            <Link to="/membership" className="text-foreground/80 hover:text-foreground transition-colors font-semibold" data-testid="nav-membership">
              Membership
            </Link>
            
            {user ? (
              <Link 
                to="/app/play" 
                className="bg-primary text-primary-foreground px-6 py-3 rounded-lg font-bold text-sm uppercase tracking-wide hover:bg-primary/90 transition-all hover:shadow-lg hover:shadow-primary/30"
                data-testid="nav-dashboard"
              >
                Enter Poker Room
              </Link>
            ) : (
              <Link 
                to="/login" 
                className="bg-primary text-primary-foreground px-6 py-3 rounded-lg font-bold text-sm uppercase tracking-wide hover:bg-primary/90 transition-all hover:shadow-lg hover:shadow-primary/30"
                data-testid="nav-login"
              >
                Sign In
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-foreground"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-2">
            <Link to="/" className="block py-2 text-foreground/80 hover:text-foreground">Home</Link>
            <Link to="/about" className="block py-2 text-foreground/80 hover:text-foreground">About</Link>
            <Link to="/lessons" className="block py-2 text-foreground/80 hover:text-foreground">Lessons</Link>
            <Link to="/calendar" className="block py-2 text-foreground/80 hover:text-foreground">Calendar</Link>
            <Link to="/contact" className="block py-2 text-foreground/80 hover:text-foreground">Contact</Link>
            <Link to="/membership" className="block py-2 text-foreground/80 hover:text-foreground">Membership</Link>
            <Link to="/live" className="block py-2 text-foreground/80 hover:text-foreground">Live</Link>
            {user ? (
              <Link to="/dashboard" className="block py-2 text-primary font-semibold">Dashboard</Link>
            ) : (
              <Link to="/login" className="block py-2 text-primary font-semibold">Sign In</Link>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}

// Import pages
import HomePage from './pages/Home';
import AboutPage from './pages/About';
import MembershipPage from './pages/Membership';
import LivePage from './pages/Live';
import DashboardRedirect from './pages/DashboardRedirect';
import CommunityPage from './pages/Community';
import LessonsPage from './pages/Lessons';
import ArchivesPage from './pages/Archives';
import AdminPage from './pages/Admin';
import LiveSessionsPage from './pages/LiveSessions';
import SkillDevelopmentPage from './pages/SkillDevelopment';
import EliteCommunityPage from './pages/EliteCommunity';
import ProCoachingPage from './pages/ProCoaching';
import PracticePage from './pages/Practice';
import BookingPage from './pages/Booking';
import PrivacyPolicyPage from './pages/PrivacyPolicy';
import TermsOfServicePage from './pages/TermsOfService';
import CommunityFirst from './pages/CommunityFirst';
import EducationFocused from './pages/EducationFocused';
import BeginnerFriendly from './pages/BeginnerFriendly';
import TexasPokerCulture from './pages/TexasPokerCulture';
import CalendarPage from './pages/Calendar';
import ContactUsPage from './pages/ContactUs';
import PokerHandsGuide from './pages/PokerHandsGuide';

// Import V4 Platform modules
import AppShell from './pages/AppShell';
import Play from './pages/Play';
import Learn from './pages/Learn';
import Watch from './pages/Watch';
import Compete from './pages/Compete';
import Profile from './pages/Profile';
import LoginPage from './pages/Login';

export { useAuth, ProtectedRoute, Navigation };

// App Router
function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<HomePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/about/community-first" element={<CommunityFirst />} />
      <Route path="/about/education-focused" element={<EducationFocused />} />
      <Route path="/calendar" element={<CalendarPage />} />
      <Route path="/contact" element={<ContactUsPage />} />
      <Route path="/about/beginner-friendly" element={<BeginnerFriendly />} />
      <Route path="/about/texas-poker-culture" element={<TexasPokerCulture />} />
      <Route path="/membership" element={<MembershipPage />} />
      <Route path="/booking" element={<BookingPage />} />
      <Route path="/live" element={<LivePage />} />
      <Route path="/practice" element={<PracticePage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      <Route path="/poker-hands" element={<PokerHandsGuide />} />
      
      {/* V4 Platform Routes (Protected with AppShell) */}
      <Route path="/app" element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        <Route path="play" element={<Play />} />
        <Route path="learn" element={<Learn />} />
        <Route path="watch" element={<Watch />} />
        <Route path="compete" element={<Compete />} />
        <Route path="profile" element={<Profile />} />
      </Route>
      
      {/* Legacy routes (still accessible) */}
      <Route path="/dashboard" element={<DashboardRedirect />} />
      <Route path="/community" element={<CommunityPage />} />
      <Route path="/community/thread/:threadId" element={<CommunityPage />} />
      <Route path="/lessons" element={<LessonsPage />} />
      <Route path="/lessons/:lessonId" element={<LessonsPage />} />
      <Route path="/poker-hands-guide" element={<PokerHandsGuide />} />
      <Route path="/archives" element={<ArchivesPage />} />
      <Route path="/admin/*" element={<AdminPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/features/live-sessions" element={<LiveSessionsPage />} />
      <Route path="/features/skill-development" element={<SkillDevelopmentPage />} />
      <Route path="/features/community" element={<EliteCommunityPage />} />
      <Route path="/features/coaching" element={<ProCoachingPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Main App
function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <AppRouter />
          {/* GoHighLevel AI Chat Widget - Add your script here */}
          <div id="ghl-chat-widget-container" data-testid="ghl-chat-widget">
            {/* Insert your GoHighLevel AI Assistant embed script here */}
          </div>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
